import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getCustomerActive } from '@/modules/Customer/services'
import { CustomerActive } from '@/modules/Customer/types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Typogaphy } from 'vnetwork-ui'
import { useState } from 'react'
import { Shield, Search, ArrowRight, Building2, FileText } from 'lucide-react'
import useDebounce from '@/hooks/useDebounce'
import { formatNumber } from '@/utils/function'

const SLACustomersWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState('')
    const debouncedSearch = useDebounce(searchQuery, 300)

    // Fetch customers with SLA
    const { data: customersData, isLoading } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.CUSTOMER,
            'sla-customers-dashboard',
            debouncedSearch,
        ],
        queryFn: () =>
            getCustomerActive({
                page: 1,
                page_size: 5,
                q: debouncedSearch,
                filters: btoa(JSON.stringify({ has_sla: true })),
            }),
    })

    const customers = customersData?.results || []
    const totalCustomers = customersData?.pagination?.total || 0

    const handleCustomerClick = (customerId: number) => {
        navigate(
            generatePathDatacentral(paths.customerDetail, { id: customerId })
        )
    }

    const handleViewAll = () => {
        navigate(
            generatePathDatacentral(
                paths.customersList,
                {},
                {
                    tab: 'active',
                    filters: btoa(JSON.stringify({ has_sla: true })),
                }
            )
        )
    }

    const renderCustomerCard = (customer: CustomerActive) => {
        const contractCount = customer.contracts?.length || 0
        const totalPayment = customer.total_payment || 0

        return (
            <div
                key={customer.customer_id}
                onClick={() => handleCustomerClick(customer.customer_id)}
                className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
            >
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <div className="p-2 rounded-full bg-blue-100 flex-shrink-0">
                        <Building2 className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs text-gray-900 truncate">
                            {customer.company_name || customer.customer_name}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500">
                            <span className="flex items-center gap-0.5">
                                <FileText className="w-3 h-3" />
                                {contractCount}{' '}
                                {t('widgets.contracts', 'hợp đồng')}
                            </span>
                            {totalPayment > 0 && (
                                <>
                                    <span>·</span>
                                    <span className="text-green-600 font-medium">
                                        {formatNumber(totalPayment)}đ
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                        SLA
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                </div>
            </div>
        )
    }

    return (
        <Paper className="h-full">
            <div className="h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-green-500" />
                        <Typogaphy className="font-semibold text-primary">
                            {t('widgets.sla_customers', 'Khách hàng có SLA')}
                        </Typogaphy>
                    </div>
                </div>

                {/* Search */}
                <div className="relative mb-3 flex-shrink-0">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                        placeholder={t(
                            'widgets.search_customer',
                            'Tìm khách hàng...'
                        )}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-8 h-9 text-sm"
                    />
                </div>

                {/* List */}
                <div className="space-y-1.5 flex-1 min-h-0 overflow-y-auto">
                    {isLoading ? (
                        <>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} className="h-14 rounded-lg" />
                            ))}
                        </>
                    ) : customers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                            <Shield className="w-10 h-10 text-gray-300 mb-2" />
                            <span className="text-sm">
                                {searchQuery
                                    ? t(
                                          'widgets.no_search_results',
                                          'Không tìm thấy kết quả'
                                      )
                                    : t(
                                          'widgets.no_sla_customers',
                                          'Chưa có khách hàng SLA'
                                      )}
                            </span>
                        </div>
                    ) : (
                        customers.map((customer) =>
                            renderCustomerCard(customer)
                        )
                    )}
                </div>

                {/* View All */}
                {totalCustomers > 5 && (
                    <div className="text-center pt-2 flex-shrink-0 border-t mt-2">
                        <button
                            onClick={handleViewAll}
                            className="text-xs text-primary hover:underline"
                        >
                            {t('widgets.view_all')} ({totalCustomers})
                        </button>
                    </div>
                )}
            </div>
        </Paper>
    )
}

export default SLACustomersWidget
